// Pure helpers and constants behind the top-nav search dropdown: section
// assembly, row identity, shortcut labels and modal bookkeeping. Kept free of
// component state so both SearchAutocomplete and the connected SearchBar class
// can share them.

import sortBy from 'lodash/sortBy'
import find from 'lodash/find'
import { getDocumentType } from '../../utils/search/getDocumentType'
import { STATIC_QUICK_ACTIONS, toQuickActionHit } from '../../services/search/local/quickActionsRegistry'
import { STATIC_NAV_ENTRIES, toNavigationHit } from '../../services/search/local/navigationRegistry'
import { EXTERNAL_LINKS, toExternalLinkHit } from '../../services/search/local/externalLinksRegistry'
import { localProviderId } from '../../services/search/presentationEngine'
import { RECENT_SEARCHES_PREFILL_COUNT } from '../../utils/search/recentSearches'

// Resolve the local set/question id a suggestion points at, for the lightweight
// preview modal (plain Enter). Returns null for anything that isn't a local set
// or question (packs/folders/nav/quick-actions/external have no modal preview).
export function getPreviewItemIdForSuggestion(suggestion) {
  if (!suggestion) return null
  if (suggestion.__recent) {
    const itemType = suggestion.__recentItemType
    if ((itemType === 'set' || itemType === 'question') && suggestion.item) return suggestion.item.id
    return null
  }
  const itemType = getDocumentType(suggestion)
  if (itemType === 'set' || itemType === 'question') return suggestion.id || suggestion._id
  return null
}

// Human-readable label for a section header.
export function getSectionLabel(index) {
  const labels = {
    repos: 'Content',
    folders: 'Content',
    // eslint-disable-next-line camelcase
    sets_questions: 'Content',
    'Recent Searches': 'Recent Searches',
    'Quick Actions': 'Quick Actions',
    Recent: 'Recent Content',
    Navigation: 'Navigation',
    'Help & External': 'Help',
  }
  return labels[index] || index
}

// Library-type section indexes. The first one encountered gets the "Library"
// header; subsequent ones use a blank header (no label, no border flicker).
const LIBRARY_INDEXES = new Set(['repos', 'folders', 'sets_questions'])

// ─── Recent helpers ────────────────────────────────────────────────────────────
// Resolve `state.recentActivities` (sorted by activityTime desc) into hit-shaped
// rows by joining against `state.sets` / `state.questions`. Items that haven't
// hydrated yet are skipped (they'll appear once the data loads).
export function buildRecentSuggestions(recentActivities, sets, questions, limit = 5) {
  if (!Array.isArray(recentActivities) || recentActivities.length === 0) return []
  const ordered = sortBy(recentActivities, 'activityTime').reverse()
  const hits = []
  for (let i = 0; i < ordered.length && hits.length < limit; i += 1) {
    const activity = ordered[i]
    if (activity && activity.item) {
      let item = null
      if (activity.itemType === 'set') {
        item = find(sets, { id: activity.item })
      } else if (activity.itemType === 'question') {
        item = find(questions, { id: activity.item })
      }
      if (item) {
        hits.push({
          id: `recent-${activity.itemType}-${item.id}`,
          objectID: `recent-${activity.itemType}-${item.id}`,
          __recent: true,
          __recentItemType: activity.itemType,
          item,
        })
      }
    }
  }
  return hits
}

// Build the recent-search rows (content-only query history) for the empty state.
export function buildRecentSearchHits(recentSearches) {
  if (!Array.isArray(recentSearches)) return []
  return recentSearches.map((query) => ({
    id: `recentSearch-${query}`,
    objectID: `recentSearch-${query}`,
    __recentSearch: true,
    query,
  }))
}

// Empty-state sections — shown when the input is focused with no query.
// Pure function of static config + Redux-derived recent items + the persisted
// recent-search query history. Order: Recent Searches → Quick Actions → Recent.
export function buildEmptyStateSections(recentSearches, recentSuggestions) {
  const sections = []
  // Show only the most recent few in the empty state; the full history is kept
  // and made searchable while typing (see SearchAutocomplete).
  const recentSearchHits = buildRecentSearchHits(
    (Array.isArray(recentSearches) ? recentSearches : []).slice(0, RECENT_SEARCHES_PREFILL_COUNT),
  )
  if (recentSearchHits.length > 0) {
    sections.push({ index: 'Recent Searches', showHeader: true, hits: recentSearchHits })
  }
  // Prefill lists — gathered from each registry by the explicit `prefill` flag.
  // Quick actions and navigation get their own headers; external entries (none
  // flagged today) fold into the Quick Actions group.
  const quickActionHits = [
    ...STATIC_QUICK_ACTIONS.filter((a) => a.prefill).map(toQuickActionHit),
    ...EXTERNAL_LINKS.filter((e) => e.prefill).map(toExternalLinkHit),
  ]
  if (quickActionHits.length > 0) {
    sections.push({ index: 'Quick Actions', showHeader: true, hits: quickActionHits })
  }
  const navHits = STATIC_NAV_ENTRIES.filter((e) => e.prefill).map(toNavigationHit)
  if (navHits.length > 0) {
    sections.push({ index: 'Navigation', showHeader: true, hits: navHits })
  }
  if (recentSuggestions.length > 0) {
    sections.push({ index: 'Recent Content', showHeader: true, hits: recentSuggestions })
  }
  return sections
}

// Merge recent-search matches + library sections + supplementary sections into
// a flat list, ordering:
//   1. Recent Searches (matches while typing)  2. Quick Actions  3. Navigation
//   4. Library  5. See all results  6. Help & External  7. Search Help Center
// Empty sections are stripped first (prevents orphan dividers).
// Library-type sections get showHeader:true only on the first occurrence.
export function buildAllSections(
  suggestions, additiveResults, currentRefinement, recentMatchHits, engine,
  { hideUpgradeQuickAction = false } = {},
) {
  const additive = Array.isArray(additiveResults) ? additiveResults : []
  const hasLibraryResults = suggestions.some((s) => s.hits && s.hits.length > 0)

  const head = []

  // ── Recent search matches (shown first while typing) ───────────────────────
  if (Array.isArray(recentMatchHits) && recentMatchHits.length > 0) {
    head.push({ index: 'Recent Searches', hits: recentMatchHits })
  }

  // ── Quick Actions ──────────────────────────────────────────────────────────
  const qaBlock = additive.find((r) => r.providerId === localProviderId('quick-actions', engine))
  if (qaBlock && Array.isArray(qaBlock.hits) && qaBlock.hits.length > 0) {
    // Drop the "Upgrade to Pro" action for users already on Pro.
    const qaFiltered = hideUpgradeQuickAction ?
      qaBlock.hits.filter((hit) => hit.action !== 'upgradePro') :
      qaBlock.hits
    // Cap quick-action results when library content is also present to avoid burying it.
    const qaHits = hasLibraryResults ? qaFiltered.slice(0, 2) : qaFiltered
    if (qaHits.length > 0) head.push({ index: 'Quick Actions', hits: qaHits })
  }

  // ── Navigation ─────────────────────────────────────────────────────────────
  const navBlock = additive.find((r) => r.providerId === localProviderId('navigation', engine))
  if (navBlock && Array.isArray(navBlock.hits) && navBlock.hits.length > 0) {
    // Cap navigation results when library content is present.
    const navHits = hasLibraryResults ? navBlock.hits.slice(0, 3) : navBlock.hits
    head.push({ index: 'Navigation', hits: navHits })
  }

  // ── Library sections: the displayed content profile ──────────────────────
  const librarySections = suggestions.filter((s) => s.hits && s.hits.length > 0)

  const extBlock = additive.find((r) => r.providerId === localProviderId('external-help', engine))
  const hasExtHits = extBlock && Array.isArray(extBlock.hits) && extBlock.hits.length > 0

  const merged = [...head, ...librarySections]

  let libraryHeaderShown = false
  const withHeaders = merged.map((section) => {
    if (LIBRARY_INDEXES.has(section.index)) {
      if (!libraryHeaderShown) {
        libraryHeaderShown = true
        return { ...section, showHeader: true }
      }
      return { ...section, showHeader: false }
    }
    return { ...section, showHeader: true }
  })

  // Footer rows appended after the header pass.
  const footers = []
  // "See all content results for …" — directly under the library item list.
  // Only shown when there are actual content (library) results to see; hidden
  // when the query has no content matches.
  if (currentRefinement && hasLibraryResults) {
    footers.push({ hits: [{ showMore: currentRefinement }], index: '', showHeader: false })
  }
  // Help & External results (matched links only — the Search Help Center row is
  // a separate, always-last footer below).
  if (hasExtHits) {
    footers.push({ index: 'Help & External', hits: extBlock.hits.slice(), showHeader: true })
  }
  // "Search Help Center" deep-link — always the last thing in the dropdown when
  // there is a query (under help results if present, otherwise just last).
  if (currentRefinement) {
    footers.push({
      index: '',
      showHeader: false,
      hits: [{ __helpShowMore: true, query: currentRefinement, objectID: 'help-show-more' }],
    })
  }

  return withHeaders.concat(footers)
}

// Platform-aware modifier label so the Cmd glyph renders on Mac and "Ctrl"
// everywhere else, wherever a cmd-based shortcut is shown.
const IS_MAC = typeof navigator !== 'undefined' &&
  navigator.platform.toUpperCase().indexOf('MAC') >= 0
// Cmd/Ctrl+Enter label shown in the input hint. Cmd/Ctrl+Enter always submits
// the entered string to the full results page ("Find content").
export const SUBMIT_SHORTCUT_LABEL = IS_MAC ? '\u2318 Enter' : 'Ctrl+Enter'
// "Open search" hint shown when the input is unfocused + empty, to advertise the
// keyboard shortcut that focuses the search field from anywhere.
export const SEARCH_SHORTCUT_LABEL = IS_MAC ? '\u2318K' : 'Ctrl+K'

// Window for telling a single click from the first click of a double click on a
// previewable dropdown row. Matches the results-page Hit timing.
export const SUGGESTION_CLICK_DELAY = 220

// Stable identity for a dropdown row. Drives the bottom shortcut hint and the
// persisted-highlight class (item retained across modals), so it must be stable
// across re-renders AND unique per visible row (no two rows share a key).
// Most rows carry an objectID; the footers and registry rows fall back to other
// stable fields.
export function shortcutHintKeyFor(s) {
  if (!s) return 'none'
  if (s.objectID != null) return `id:${s.objectID}`
  if (s.showMore != null) return `showMore:${s.showMore}`
  if (s.__helpShowMore) return 'helpShowMore'
  if (s.id != null) return `id:${s.id}`
  if (s._id != null) return `id:${s._id}`
  if (s.path != null) return `path:${s.path}`
  if (s.url != null) return `url:${s.url}`
  if (s.query != null) return `q:${s.query}`
  if (s.label != null) return `label:${s.label}`
  return 'unknown'
}

// Bottom-of-dropdown shortcut line, tailored to the highlighted row. Returns an
// ordered list of { keys, label } hints. Always begins with ↑↓ navigate and
// never advertises ⌘K or ⌘+Enter (the input itself now carries those hints: ⌘K
// when unfocused/empty, ⌘Enter "Submit" when there's a typed query). Rows
// without a modal preview — quick actions, navigation, external links, packs and
// folders — advertise the single action Enter performs on them ("open"/"go"/
// "search") instead of the preview / double-Enter pair.
export function getDropdownShortcutHints(highlightedSuggestion) {
  const hints = [{ keys: '↑↓', label: 'navigate' }]
  const s = highlightedSuggestion

  if (!s || s.showMore || s.__helpShowMore) {
    hints.push({ keys: 'Enter', label: 'all results' })
    return hints
  }
  if (s.__recentSearch) {
    hints.push({ keys: 'Enter', label: 'search' })
    return hints
  }
  if (s.__quickAction) {
    hints.push({ keys: 'Enter', label: 'open' })
    return hints
  }
  if (s.__nav) {
    hints.push({ keys: 'Enter', label: 'go' })
    return hints
  }
  if (s.__external) {
    hints.push({ keys: 'Enter', label: 'open' })
    return hints
  }
  // Only sets and questions have a modal preview. Packs and folders do render a
  // preview pane, but Enter opens them directly, so they must not advertise the
  // preview / double-Enter pair.
  if (getPreviewItemIdForSuggestion(s)) {
    // First Enter opens the preview; a second Enter (inside the preview) opens
    // the item — so "open" advertises two Enter keys.
    hints.push({ keys: 'Enter', label: 'preview' })
    hints.push({ keys: ['Enter', 'Enter'], label: 'open' })
    return hints
  }
  // Remaining library entities without a modal preview (pack/folder).
  if (getDocumentType(s)) {
    hints.push({ keys: 'Enter', label: 'open' })
    return hints
  }
  hints.push({ keys: 'Enter', label: 'all results' })
  return hints
}

// Hash fragments that mean the hash-driven Upgrade-to-Pro modal is open. Unlike
// New Pack / New Class (redux modals surfaced via `currentModal`), the upgrade
// modal is controlled purely by the URL hash (see UpgradeContainer), so its
// open/close is detected from the location hash rather than redux state.
const UPGRADE_MODAL_HASHES = ['#upgrade', '#upgrade-payment']
export function isUpgradeModalHash(hash) {
  return UPGRADE_MODAL_HASHES.indexOf(hash || '') !== -1
}

// Map a quick-action token to the modal it opens. Any quick action with a modal
// type keeps the dropdown alive behind that modal (parity across New Pack / New
// Class / Upgrade to Pro); actions without one (e.g. New Set) close it.
export function quickActionModalType(action) {
  if (action === 'newRepo') return 'NEW_REPO_MODAL'
  if (action === 'newSection') return 'NEW_SECTION_MODAL'
  if (action === 'upgradePro') return 'UPGRADE_MODAL'
  return null
}
